"use client";

import { format } from "date-fns";
import * as React from "react";

import {
  PopoverMenu,
  PopoverMenuItem,
  PopoverMenuSection,
} from "@/components/ui/popover-menu";
import { cn } from "@/lib/utils";

import { SystemDateTimeFields } from "../catalog/system-datetime-fields";

type Props = {
  /** ISO string with timezone, or null for manual (no scheduled start). */
  value: string | null;
  onChange: (next: string | null) => void;
  trigger: React.ReactElement;
  disabled?: boolean;
};

type StartMode = "manual" | "now" | "scheduled";

function pad2(n: number): string {
  return n.toString().padStart(2, "0");
}

function toDateInputValue(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function toTimeInputValue(d: Date): string {
  return `${pad2(d.getHours())}:00`;
}

function defaultScheduledAt(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(9, 0, 0, 0);
  return d.toISOString();
}

function commitDateTime(dateStr: string, timeStr: string): string | null {
  const [y, m, day] = dateStr.split("-").map((p) => Number.parseInt(p, 10));
  const [hh, mm] = timeStr.split(":").map((p) => Number.parseInt(p, 10));
  if (
    !Number.isFinite(y) ||
    !Number.isFinite(m) ||
    !Number.isFinite(day) ||
    !Number.isFinite(hh) ||
    !Number.isFinite(mm)
  ) {
    return null;
  }
  const out = new Date(y, m - 1, day, hh, mm, 0, 0);
  if (Number.isNaN(out.getTime())) return null;
  return out.toISOString();
}

function modeFromStartAt(startAt: string | null): StartMode {
  if (!startAt) return "manual";
  const ms = Date.parse(startAt);
  if (Number.isNaN(ms)) return "manual";
  if (ms > Date.now()) return "scheduled";
  if (Date.now() - ms < 60_000) return "now";
  return "scheduled";
}

function scheduledDraftFromStartAt(startAt: string | null): {
  date: string;
  time: string;
} {
  const base = startAt ? new Date(startAt) : new Date(defaultScheduledAt());
  const d = Number.isNaN(base.getTime()) ? new Date(defaultScheduledAt()) : base;
  return { date: toDateInputValue(d), time: toTimeInputValue(d) };
}


/**
 * Start-at picker with three modes: manual, start now, or a specific date/time.
 * Uses the shared PopoverMenu surface — no calendar widget.
 */
export function TaskStartAtMenu({ value, onChange, trigger, disabled }: Props) {
  const [open, setOpen] = React.useState(false);
  const mode = modeFromStartAt(value);
  const scheduleDraft = React.useMemo(
    () => scheduledDraftFromStartAt(value),
    [value],
  );

  const selectManual = () => {
    onChange(null);
    setOpen(false);
  };

  const selectNow = () => {
    onChange(new Date().toISOString());
    setOpen(false);
  };

  const selectScheduled = () => {
    const next = value && mode === "scheduled" ? value : defaultScheduledAt();
    onChange(next);
  };

  const updateSchedule = (nextDate: string, nextTime: string) => {
    const committed = commitDateTime(nextDate, nextTime);
    if (committed) onChange(committed);
  };

  return (
    <PopoverMenu
      open={open}
      onOpenChange={disabled ? undefined : setOpen}
      trigger={trigger}
      menuContentClassName="w-[15.125rem] p-0"
      menu={
        <>
          <PopoverMenuSection
            className={cn("px-1 pt-1", mode !== "scheduled" && "pb-1.5")}
          >
            <PopoverMenuItem
              label="Manual"
              selected={mode === "manual"}
              disabled={disabled}
              onClick={selectManual}
            />
            <PopoverMenuItem
              label="Start now"
              selected={mode === "now"}
              disabled={disabled}
              onClick={selectNow}
            />
            <PopoverMenuItem
              label="Start on date at time"
              selected={mode === "scheduled"}
              disabled={disabled}
              onClick={selectScheduled}
            />
          </PopoverMenuSection>

          {mode === "scheduled" ? (
            <PopoverMenuSection className="px-1 pb-1.5">
              <SystemDateTimeFields
                date={scheduleDraft.date}
                time={scheduleDraft.time}
                disabled={disabled}
                onDateChange={(nextDate) =>
                  updateSchedule(nextDate, scheduleDraft.time)
                }
                onTimeChange={(nextTime) =>
                  updateSchedule(scheduleDraft.date, nextTime)
                }
              />
            </PopoverMenuSection>
          ) : null}
        </>
      }
    />
  );
}

/** Short display for a scheduled start-at chip (e.g. "May 23, 2:30 PM"). */
export function formatStartAtShort(value: string | null): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return format(d, "MMM d, h:mm a");
}

/** Chip label across manual / now / scheduled modes. */
export function formatStartAtChipLabel(
  value: string | null,
  emptyLabel = "Manual",
): string {
  const mode = modeFromStartAt(value);
  if (mode === "manual") return emptyLabel;
  if (mode === "now") return "Start now";
  return formatStartAtShort(value) ?? "Start on date at time";
}
